// inv/src/pages/authentication/SignUpPage.jsx
import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import SignUpGoogleButton from './SignUpGoogleButton';
import PlansExtraInfo from '../payment/planExtraInfo';
import { API_URL } from '../../config';
import { Mail, Lock, LockKeyhole, MessageCircleCode } from 'lucide-react';
import { collectGAContext } from '../../service-scripts/gaContext';

function buildDashboardWelcomePath(language) {
  const languagePrefix = language === 'en' ? '' : `/${language}`;
  const params = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();

  params.set('welcome', '1');
  const search = params.toString();

  return `${languagePrefix}/app/dashboard${search ? `?${search}` : ''}`;
}

const SignUpPage = () => {
  const { t, i18n } = useTranslation();
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userExists, setUserExists] = useState(false);
  const [googleUser, setGoogleUser] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmationNeeded, setConfirmationNeeded] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationError, setVerificationError] = useState(false);

  const validatePasswords = () => {
    setPasswordsMatch(password === confirmPassword);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setUserExists(false);
    setGoogleUser(false);
    setConfirmationNeeded(false);
    setVerificationError(false);
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    if (!passwordsMatch || !termsAccepted) {
      return;
    }
    try {
      const { client_id, attribution } = await collectGAContext();

      const url = client_id
        ? `${API_URL}/signup-form?client_id=${encodeURIComponent(client_id)}`
        : `${API_URL}/signup-form`;

      const response = await axios.post(
        url,
        { email, password, attribution },
        { withCredentials: true }
      );
      console.log('Server response:', response.data);

      if (response.data.message === 'Email confirmation required') {
        setConfirmationNeeded(true);
      } else {
        setRegistrationSuccess(true);
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response && error.response.status === 400) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('Google')) {
          setGoogleUser(true);
        } else if (errorMessage.includes('зарегистрирован')) {
          setUserExists(true);
        }
      }
    }
  };

  const handleConfirmationSubmit = async (event) => {
    event.preventDefault();
    console.log('Отправка данных для верификации:', { email, confirmationCode });

    try {
      const { client_id, attribution } = await collectGAContext();

      const url = client_id
        ? `${API_URL}/signup-verify-email?client_id=${encodeURIComponent(client_id)}`
        : `${API_URL}/signup-verify-email`;

      const response = await axios.post(
        url,
        { email, verificationCode: confirmationCode, attribution },
        { withCredentials: true }
      );

      console.log('Ответ сервера:', response.data);

      window.location.href = buildDashboardWelcomePath(i18n.language);
    } catch (error) {
      console.error('Ошибка подтверждения регистрации:', error);
      setVerificationError(true);
    }
  };

  return (
    <div className="signup-wrapper w-full">
      <div className="flex container px-12 md:px-12 lg:px-18 mt-8 min-h-[520px]">
        <div className="quote-section1 shadow-lg rounded-md px-12 w-1/3 hidden lg:flex flex-col items-center justify-center bg-gray-100">
          <p className="quote-text">
            "The value of an idea lies in the using of it."</p>
          <p className="quote-author">— Thomas Edison, General Electric Co-founder</p>
        </div>

        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center py-8 mx-auto lg:py-0">
          <div className="w-full bg-white rounded-lg sm:max-w-md xl:p-0">
            <div className="px-2 space-y-4 md:space-y-6 sm:p-8">
              <h2 className="text-left text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl ">
                {confirmationNeeded ? t('pageSignUp.t17') : t('pageSignUp.t1')}
              </h2>

              <form
                className="space-y-4 md:space-y-6"
                onSubmit={confirmationNeeded ? handleConfirmationSubmit : handleSignUp}
              >
                {confirmationNeeded ? (
                  <div className="items-center">
                    <div className="flex mb-4 items-center">
                      <MessageCircleCode className="w-5 h-5 mr-2" />
                      <label
                        htmlFor="confirmation-code"
                        className="w-2/3 block text-sm font-medium text-gray-900 "
                      >
                        {t('pageSignUp.t10')}
                      </label>
                    </div>

                    <Input
                      className="w-2/3"
                      multi={false}
                      value={confirmationCode}
                      onChange={(e) => setConfirmationCode(e.target.value)}
                      placeholder={t('pageSignUp.t11')}
                      name="confirmation-code"
                      id="confirmation-code"
                      required
                    />

                    <div className="mt-6">
                      <p className="w-2/3 block text-sm font-medium text-gray-900 ">
                        * Check your spam folder if you don't see the email.
                      </p>
                    </div>

                    {verificationError && (
                      <p className="text-red-500">{t('pageSignUp.t12')}</p>
                    )}

                    <Button
                      className="mt-8 min-w-36"
                      type="submit"
                      variant="secondary"
                      size="sm"
                    >
                      {t('pageSignUp.t13')}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 mr-2" />
                      <label
                        htmlFor="email"
                        className="w-1/3 text-sm font-medium text-gray-900 "
                      >
                        {t('pageSignUp.t2')}
                      </label>
                      <Input
                        className="w-2/3"
                        multi={false}
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        name="email"
                        id="email"
                        placeholder="name@company.com"
                        required
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Lock className="w-5 h-5 mr-2" />
                      <label
                        htmlFor="password"
                        className="w-1/3 block text-sm font-medium text-gray-900 "
                      >
                        {t('pageSignUp.t3')}
                      </label>
                      <Input
                        className="w-2/3"
                        multi={false}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        name="password"
                        id="password"
                        placeholder="••••••••"
                        required
                      />
                    </div>

                    <div>
                      <p className="w-3/3 block text-sm font-medium text-gray-900 ">
                        * {t('pageSignUp.t19')}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <LockKeyhole className="w-5 h-5 mr-2" />
                      <label
                        htmlFor="confirm-password"
                        className="w-1/3 block text-sm font-medium text-gray-900 "
                      >
                        {t('pageSignUp.t4')}
                      </label>
                      <Input
                        className="w-2/3"
                        multi={false}
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={validatePasswords}
                        name="confirm-password"
                        id="confirm-password"
                        placeholder="••••••••"
                        required
                      />
                      {!passwordsMatch && (
                        <p className="text-red-500">{t('pageSignUp.t14')}</p>
                      )}
                    </div>

                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <Checkbox
                          id="terms"
                          aria-describedby="terms"
                          checked={termsAccepted}
                          onCheckedChange={(checked) => setTermsAccepted(!!checked)}
                          required
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label
                          htmlFor="terms"
                          className="font-light text-gray-500"
                        >
                          {t('pageSignUp.t5')}{' '}
                          <a
                            href={
                              i18n.language === 'en'
                                ? '/terms-and-conditions'
                                : `/${i18n.language}/terms-and-conditions`
                            }
                            className="text-gray-600  hover:text-gray-900 transition duration-150 ease-in-out"
                          >
                            {t('footer.termsOfUse')}
                          </a>
                        </label>
                      </div>
                    </div>

                    {userExists && (
                      <p className="text-red-500">{t('pageSignUp.t15')}</p>
                    )}
                    {googleUser && (
                      <p className="text-red-500">{t('pageSignUp.t16')}</p>
                    )}

                    <Button
                      className="min-w-36"
                      type="submit"
                      variant="secondary"
                      size="sm"
                    >
                      {t('pageSignUp.t1')}
                    </Button>
                  </>
                )}
              </form>

              {!confirmationNeeded && (
                <div className="mt-4 space-y-3">
                  <SignUpGoogleButton />
                  <p className="text-sm font-light text-gray-500 ">
                    {t('pageSignUp.t8')}{' '}
                    <a
                      href={
                        i18n.language === 'en'
                          ? '/login'
                          : `/${i18n.language}/login`
                      }
                      className="font-medium text-stone-900 hover:underline "
                    >
                      {t('pageSignUp.t9')}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <PlansExtraInfo />
    </div>
  );
}

export default SignUpPage;
